import { inject } from "@angular/core";
import { CanMatchFn, RedirectCommand, Router } from "@angular/router";
import { filter, map, switchMap, take } from "rxjs";
import { AppPath } from "../../app.routes";
import { AuthPath } from "../../features/auth/auth.routes";
import { SignupSteps } from "../../features/auth/signup/signup.routes";
import { SubscriptionService } from "../subscription.service";
import { AuthService } from '../auth/auth.service';

/**
 * Allows navigation if the user has had at least one subscription in the past,
 * otherwise, redirects them to the plan selection page.
 *
 * This guard should only be used when we already know the user has been authenticated.
 * For this we `must` place the `isAuthenticated` before this one.
 * i.e. canMatch: [isAuthenticated(), hasPlanGuard()]
 */
export const isSubscriberGuard = (): CanMatchFn => () => {
  const auth = inject(AuthService);
  const router = inject(Router);
  const subscriptions = inject(SubscriptionService);

  const planSelectionCommand = ['/', AppPath.Auth, AuthPath.SignUp, SignupSteps.PlanSelection];
  const planSelectionTree = router.createUrlTree(planSelectionCommand);
  const redirectToPlanSelection = new RedirectCommand(planSelectionTree);

  return auth.getUser().pipe(
    filter(Boolean),
    take(1),
    switchMap(user =>
      subscriptions.getSubscriptionHistory(user.uid).pipe(take(1))
    ),
    map(subscriptions => subscriptions.length > 0 || redirectToPlanSelection),
  );
}
